import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Edit, UserPlus } from 'lucide-react';
import Modal, { ModalFormGrid, ModalFormColumn, FormGroup } from '../components/common/Modal';
import NotificationSystem, { useNotification } from '../components/common/NotificationSystem';
import CustomSelect from '../components/common/CustomSelect';
import userService from '../services/userService';
import { ROLES, ROLE_LABELS } from '../config/permissions';
import { useAuth } from '../context/AuthContext';
import '../styles/FormSections.css';

const ROLE_OPTIONS = Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label }));

const UserListPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [filterRole, setFilterRole] = useState('');
  const [filterActive, setFilterActive] = useState('');

  const { notification, showNotification } = useNotification();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  const canCreate = currentUser?.role === ROLES.SUPER_ADMIN || currentUser?.role === ROLES.GERENTE;
  const canEdit = canCreate;
  const canDeactivate = canCreate;

  const loadUsers = useCallback(async (page = 1, search = '') => {
    try {
      setLoading(true);
      const params = { page, limit: itemsPerPage, search };
      if (filterRole) params.role = filterRole;
      if (filterActive !== '') params.isActive = filterActive === 'true';

      const response = await userService.getUsers(params);
      setUsers(response.users || []);
      setTotalPages(response.totalPages || 1);
      setTotalItems(response.totalItems || 0);
      setCurrentPage(page);
    } catch (error) {
      showNotification(error.message || 'Error al cargar usuarios', 'error');
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, filterRole, filterActive, showNotification]);

  useEffect(() => {
    loadUsers(1, searchTerm);
  }, [loadUsers, searchTerm]);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: ROLES.ASESOR_COMERCIAL,
    isActive: true,
    personal: { firstName: '', lastName: '', documentType: '', documentNumber: '' },
    contact: { phone: '', mobile: '' },
    employment: { position: '', department: '' }
  });

  const openCreateModal = () => {
    setFormData({
      email: '',
      password: '',
      role: ROLES.ASESOR_COMERCIAL,
      isActive: true,
      personal: { firstName: '', lastName: '', documentType: '', documentNumber: '' },
      contact: { phone: '', mobile: '' },
      employment: { position: '', department: '' }
    });
    setIsEditing(false);
    setEditingUser(null);
    setShowModal(true);
  };

  const openEditModal = (user) => {
    if (user) {
      setFormData({
        email: user.email,
        password: '',
        role: user.role,
        isActive: user.isActive,
        personal: user.personal || { firstName: '', lastName: '', documentType: '', documentNumber: '' },
        contact: user.contact || { phone: '', mobile: '' },
        employment: user.employment || { position: '', department: '' }
      });
      setIsEditing(true);
      setEditingUser(user);
      setShowModal(true);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setIsEditing(false);
    setEditingUser(null);
  };

  const updateFormField = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (isEditing && !payload.password) delete payload.password;
      if (!isEditing && !payload.password) {
        showNotification('La contraseña es requerida para nuevos usuarios', 'error');
        return;
      }

      if (isEditing && editingUser) {
        await userService.updateUser(editingUser._id, payload);
        showNotification('Usuario actualizado exitosamente', 'success');
      } else {
        await userService.createUser(payload);
        showNotification('Usuario creado exitosamente', 'success');
      }
      loadUsers(currentPage, searchTerm);
      closeModal();
    } catch (error) {
      showNotification(error.message || 'Error al guardar usuario', 'error');
    }
  };

  const handleDeactivate = async (user) => {
    if (user.role === ROLES.SUPER_ADMIN) {
      showNotification('No se puede desactivar al Super Administrador', 'error');
      return;
    }
    if (!window.confirm(`¿Desactivar a ${user.fullName || user.email}?`)) return;
    try {
      await userService.deactivateUser(user._id);
      showNotification('Usuario desactivado', 'success');
      loadUsers(currentPage, searchTerm);
    } catch (error) {
      showNotification(error.message || 'Error al desactivar', 'error');
    }
  };

  const handleViewProfile = (userId) => navigate(`/users/${userId}`);

  const roleOptionsForCreate = ROLE_OPTIONS.filter(
    r => currentUser?.role === ROLES.SUPER_ADMIN || r.value !== ROLES.SUPER_ADMIN
  );

  return (
    <div className="animate-fadeIn">
      <NotificationSystem notification={notification} onClose={() => {}} />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Gestión de Usuarios</h1>
          <p className="text-gray-600">
            Administra usuarios, roles y accesos del sistema
          </p>
        </div>
        {canCreate && (
          <button className="btn btn-primary" onClick={openCreateModal}>
            <UserPlus size={18} className="btn-icon" />
            Nuevo Usuario
          </button>
        )}
      </div>

      {/* Filtros - mismo estilo que Clientes */}
      <div className="card mb-6 filters-container">
        <div className="card-content">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-64">
              <label className="form-label">Buscar</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nombre o email..."
                className="form-input"
              />
            </div>
            <div className="w-48">
              <CustomSelect
                label="Rol"
                value={filterRole}
                onChange={(v) => setFilterRole(v)}
                options={[{ value: '', label: 'Todos los roles' }, ...ROLE_OPTIONS]}
                placeholder="Todos los roles"
              />
            </div>
            <div className="w-48">
              <CustomSelect
                label="Estado"
                value={filterActive}
                onChange={(v) => setFilterActive(v)}
                options={[
                  { value: '', label: 'Todos' },
                  { value: 'true', label: 'Activos' },
                  { value: 'false', label: 'Inactivos' }
                ]}
                placeholder="Todos"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="table-container">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="loading-spinner"></div>
            <p className="mt-4 text-gray-600">Cargando usuarios...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-6xl mb-4">👤</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm || filterRole || filterActive ? 'No se encontraron resultados' : 'No hay usuarios'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || filterRole || filterActive
                ? 'No hay usuarios que coincidan con los filtros aplicados'
                : 'Comienza agregando el primer usuario'}
            </p>
            {canCreate && !(searchTerm || filterRole || filterActive) && (
              <button onClick={openCreateModal} className="btn btn-primary">
                <UserPlus size={18} className="btn-icon" />
                Nuevo Usuario
              </button>
            )}
          </div>
        ) : (
          <table className="table">
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Email</th>
                    <th>Rol</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u._id}>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm font-semibold">
                            {(u.personal?.firstName?.[0] || u.email[0] || '?').toUpperCase()}
                          </div>
                          {u.fullName || u.email}
                        </div>
                      </td>
                      <td>{u.email}</td>
                      <td><span className="badge badge-info">{u.roleLabel || ROLE_LABELS[u.role]}</span></td>
                      <td>
                        <span className={`badge ${u.isActive ? 'badge-success' : 'badge-secondary'}`}>
                          {u.isActive ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => handleViewProfile(u._id)}
                            title="Ver perfil"
                          >
                            <Eye size={16} />
                          </button>
                          {canEdit && (currentUser?.role === ROLES.SUPER_ADMIN || u.role !== ROLES.SUPER_ADMIN) && (
                            <button
                              className="btn btn-sm btn-outline"
                              onClick={() => openEditModal(u)}
                              title="Editar"
                            >
                              <Edit size={16} />
                            </button>
                          )}
                          {canDeactivate && u.role !== ROLES.SUPER_ADMIN && u.isActive && (
                            <button
                              className="btn btn-sm btn-outline text-danger"
                              onClick={() => handleDeactivate(u)}
                              title="Desactivar"
                            >
                              Desactivar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
        )}
        {!loading && users.length > 0 && totalPages > 1 && (
          <div className="flex justify-between items-center p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <span className="text-sm text-gray-600">
              Mostrando {users.length} de {totalItems} usuarios
            </span>
            <div className="flex gap-2">
              <button
                className="btn btn-sm btn-outline"
                disabled={currentPage <= 1}
                onClick={() => loadUsers(currentPage - 1, searchTerm)}
              >
                Anterior
              </button>
              <span className="py-2 px-3 text-sm">
                Página {currentPage} de {totalPages}
              </span>
              <button
                className="btn btn-sm btn-outline"
                disabled={currentPage >= totalPages}
                onClick={() => loadUsers(currentPage + 1, searchTerm)}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}
        onSubmit={handleSubmit}
      >
        <ModalFormGrid>
          <ModalFormColumn>
            <div className="form-section">
              <h4 className="form-section-title">Credenciales</h4>
              <FormGroup label="Email" required>
                <input
                  type="email"
                  className="form-input"
                  value={formData.email}
                  onChange={(e) => updateFormField('email', e.target.value)}
                  placeholder="email@empresa.com"
                  disabled={isEditing}
                  required
                />
              </FormGroup>
              <FormGroup label={isEditing ? 'Nueva contraseña (dejar vacío para mantener)' : 'Contraseña'} required={!isEditing}>
                <input
                  type="password"
                  className="form-input"
                  value={formData.password}
                  onChange={(e) => updateFormField('password', e.target.value)}
                  placeholder="••••••••"
                  minLength={6}
                />
              </FormGroup>
              <FormGroup label="Rol" required>
                <CustomSelect
                  options={roleOptionsForCreate}
                  value={formData.role}
                  onChange={(v) => updateFormField('role', v)}
                />
              </FormGroup>
              {isEditing && (
                <FormGroup label="Estado">
                  <CustomSelect
                    options={[{ value: true, label: 'Activo' }, { value: false, label: 'Inactivo' }]}
                    value={formData.isActive}
                    onChange={(v) => updateFormField('isActive', v)}
                  />
                </FormGroup>
              )}
            </div>
          </ModalFormColumn>
          <ModalFormColumn>
            <div className="form-section">
              <h4 className="form-section-title">Datos Personales</h4>
              <FormGroup label="Nombre">
                <input
                  className="form-input"
                  value={formData.personal?.firstName || ''}
                  onChange={(e) => updateFormField('personal.firstName', e.target.value)}
                  placeholder="Nombre"
                />
              </FormGroup>
              <FormGroup label="Apellido">
                <input
                  className="form-input"
                  value={formData.personal?.lastName || ''}
                  onChange={(e) => updateFormField('personal.lastName', e.target.value)}
                  placeholder="Apellido"
                />
              </FormGroup>
              <FormGroup label="Teléfono">
                <input
                  className="form-input"
                  value={formData.contact?.phone || ''}
                  onChange={(e) => updateFormField('contact.phone', e.target.value)}
                  placeholder="Teléfono"
                />
              </FormGroup>
            </div>
            <div className="form-section">
              <h4 className="form-section-title">Laboral</h4>
              <FormGroup label="Cargo">
                <input
                  className="form-input"
                  value={formData.employment?.position || ''}
                  onChange={(e) => updateFormField('employment.position', e.target.value)}
                  placeholder="Cargo"
                />
              </FormGroup>
              <FormGroup label="Departamento">
                <input
                  className="form-input"
                  value={formData.employment?.department || ''}
                  onChange={(e) => updateFormField('employment.department', e.target.value)}
                  placeholder="Departamento"
                />
              </FormGroup>
            </div>
          </ModalFormColumn>
        </ModalFormGrid>
      </Modal>
    </div>
  );
};

export default UserListPage;
